import vacantLandParcelService from "@/views/vacantLandParcel/vacantLandParcelService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";

import type {
  VacantLandParcel,
  VacantLandParcelNew,
  VacantLandParcelQuery,
  CanDeleteVacantLandParcel,
} from "./vacantLandParcel";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "vacant-land-parcels";
const ENTITY_NAME = "Vacant Land Parcel";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<VacantLandParcel>(apiHosts.AQMS, axiosParams);

const getVacantLandParcels = (params: VacantLandParcelQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getVacantLandParcelById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

type VacantLandParcelIds = {
  id?: string | number;
};
const getNewVacantLandParcel = async (ids: VacantLandParcelIds) => {
  const defaultApp = {
    data: {
      ...vacantLandParcelService.getInitialData(),
      ...ids,
      mailCountryMurn: LE.Country.USA,
    },
  } as AxiosResponse<VacantLandParcelNew, any>;

  return defaultApp;
};

const createVacantLandParcel = (vacantLandParcel: VacantLandParcel, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, vacantLandParcel, {
    successToastCb,
    errorToastCb,
  });
};

const updateVacantLandParcel = (vacantLandParcel: VacantLandParcel, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + vacantLandParcel.id, vacantLandParcel, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {VacantLandParcel} vacantLandParcel This cannot be a 'ref' to a vacantLandParcel
 */
const saveVacantLandParcel = (vacantLandParcel: VacantLandParcel, config = {}) => {
  if (vacantLandParcel.id) {
    return updateVacantLandParcel(vacantLandParcel, config);
  } else {
    return createVacantLandParcel(vacantLandParcel, config);
  }
};

const deleteVacantLandParcelById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const canDeleteApi = apiService<CanDeleteVacantLandParcel, Paging<CanDeleteVacantLandParcel>>(
  apiHosts.AQMS,
  axiosParams,
);
const canDeleteVacantLandParcel = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return canDeleteApi.get(URL + "/can-delete/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteVacantLandParcelById,
  getVacantLandParcels,
  getVacantLandParcelById,
  getNewVacantLandParcel,
  createVacantLandParcel,
  updateVacantLandParcel,
  saveVacantLandParcel,
  canDeleteVacantLandParcel,
};
